import {DateTime} from 'luxon';
import {createParser, createSerializer, useQueryStates} from 'nuqs';
import {useCallback, useMemo} from 'react';

export const URL_KEYS_MAP = {
  dateTimeFrom: 'from',
  dateTimeTo: 'to',
} as const;

const parseDateTime = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const dt = DateTime.fromISO(value, {zone: 'utc'});
  return dt.isValid ? dt.toISO() : null;
};

const isoStringParser = createParser({
  parse: value => parseDateTime(value),
  serialize: value => value || '',
});

const defaultSerializerParams = [
  {
    dateTimeFrom: isoStringParser,
    dateTimeTo: isoStringParser,
  },
  {
    urlKeys: URL_KEYS_MAP,
  },
] as Parameters<typeof createSerializer>;

export const serialize = createSerializer(...defaultSerializerParams);

const useDateTimeParams = () => {
  const [{dateTimeFrom, dateTimeTo}, setDateTime] = useQueryStates(
    ...defaultSerializerParams,
  );

  const {parsedDateTimeFrom, parsedDateTimeTo} = useMemo(() => {
    if (!dateTimeFrom || !dateTimeTo) {
      return {parsedDateTimeFrom: null, parsedDateTimeTo: null};
    }

    const parsedDateTimeFrom = DateTime.fromISO(dateTimeFrom);
    const parsedDateTimeTo = DateTime.fromISO(dateTimeTo);

    if (!parsedDateTimeFrom.isValid || !parsedDateTimeTo.isValid) {
      return {parsedDateTimeFrom: null, parsedDateTimeTo: null};
    }

    return {parsedDateTimeFrom, parsedDateTimeTo};
  }, [dateTimeFrom, dateTimeTo]);

  const setDateTimeParams = useCallback(
    ({
      dateTimeFrom,
      dateTimeTo,
    }: {
      dateTimeFrom: string | null;
      dateTimeTo: string | null;
    }) => {
      setDateTime(() => ({
        dateTimeFrom: parseDateTime(dateTimeFrom),
        dateTimeTo: parseDateTime(dateTimeTo),
      }));
    },
    [setDateTime],
  );

  const resetDateTimeParams = useCallback(() => {
    setDateTimeParams({
      dateTimeFrom: null,
      dateTimeTo: null,
    });
  }, [setDateTimeParams]);

  return {
    dateTimeFrom,
    dateTimeTo,
    parsedDateTimeFrom,
    parsedDateTimeTo,
    resetDateTimeParams,
    setDateTimeParams,
    serialize,
  };
};

export default useDateTimeParams;
